// src/pages/admin/SuperAdmin.js
import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, query, where, addDoc,
  serverTimestamp, doc, updateDoc, setDoc,
} from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { getApp, getApps, initializeApp } from "firebase/app";
import { isModuleOn } from "../../utils/modules";

// D-301: a SEPARATE Firebase app instance used only to create logins.
// createUserWithEmailAndPassword() signs the new user in on whichever auth
// it is given — on the main app that replaced the SuperAdmin's own session,
// so the users-doc write that followed was made as the new (claim-less) user
// and failed with "Missing or insufficient permissions", leaving a login
// with no profile. Same config as the main app, own session.
const USER_CREATOR_APP = "zeno-user-creator";
const getUserCreatorAuth = () => {
  const app = getApps().find(a => a.name === USER_CREATOR_APP)
    || initializeApp(getApp().options, USER_CREATOR_APP);
  return getAuth(app);
};

const SAAS_OWNER_UID = "tbYpXinLm6eT3zdvCmpEFyEN4R13";
const user = () => JSON.parse(localStorage.getItem("user") || "{}");

export default function SuperAdmin() {
  const me = user();
  const myUid = me.uid || me.id || "";
  if (myUid !== SAAS_OWNER_UID) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9" }}>
        <div style={{ textAlign: "center", padding: "48px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <p style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>Access Denied</p>
          <p style={{ color: "#64748b", marginTop: "8px" }}>This page is restricted to SAAS admin only.</p>
        </div>
      </div>
    );
  }
  return <SuperAdminPanel />;
}

function SuperAdminPanel() {
  const [companies,       setCompanies]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [activeTab,       setActiveTab]       = useState("companies");
  const [showUserModal,   setShowUserModal]   = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [selectedComp,    setSelectedComp]    = useState(null);
  const [compUsers,       setCompUsers]       = useState([]);
  const [usersLoading,    setUsersLoading]    = useState(false);

  const [compForm, setCompForm] = useState({
    company_name: "", contact_name: "", contact_phone: "", contact_email: "",
    business_type: "", plan: "basic",
  });
  const [compSaving, setCompSaving] = useState(false);

  const [userForm, setUserForm] = useState({
    name: "", email: "", password: "Welcome@123", role: "owner", phone: "",
  });
  const [userSaving, setUserSaving] = useState(false);
  const [userMsg,    setUserMsg]    = useState("");

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "companies"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setCompanies(list);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCompanyUsers = async (company_id) => {
    setUsersLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("company_id", "==", company_id)));
      setCompUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setUsersLoading(false); }
  };

  const handleCreateCompany = async () => {
    if (!compForm.company_name.trim()) { alert("Company name required"); return; }
    if (!compForm.contact_name.trim()) { alert("Contact name required"); return; }
    setCompSaving(true);
    try {
      const ref = await addDoc(collection(db, "companies"), {
        company_name:  compForm.company_name.trim(),
        contact_name:  compForm.contact_name.trim(),
        contact_phone: compForm.contact_phone.trim(),
        contact_email: compForm.contact_email.trim(),
        business_type: compForm.business_type.trim(),
        plan:          compForm.plan,
        status:        "active",
        created_at:    serverTimestamp(),
        created_by:    SAAS_OWNER_UID,
      });
      alert(`Company created ✅\ncompany_id: ${ref.id}`);
      setCompForm({ company_name: "", contact_name: "", contact_phone: "", contact_email: "", business_type: "", plan: "basic" });
      fetchCompanies();
      setActiveTab("companies");
    } catch (err) { alert("Error: " + err.message); }
    finally { setCompSaving(false); }
  };

  const handleCreateUser = async () => {
    if (!userForm.name.trim())  { setUserMsg("Name required"); return; }
    if (!userForm.email.trim()) { setUserMsg("Email required"); return; }
    if (!userForm.password || userForm.password.length < 6) { setUserMsg("Password min 6 chars"); return; }
    if (!selectedComp) { setUserMsg("No company selected"); return; }
    setUserSaving(true);
    setUserMsg("");
    try {
      // D-301: login created on the separate instance — the SuperAdmin stays
      // signed in on the main app, so the users doc below is written with
      // the SuperAdmin's permission.
      const creatorAuth = getUserCreatorAuth();
      const { user } = await createUserWithEmailAndPassword(creatorAuth, userForm.email.trim(), userForm.password);
      await signOut(creatorAuth);
      // Doc id = auth uid, same as every other owner/manager users doc.
      await setDoc(doc(db, "users", user.uid), {
        uid:        user.uid,
        name:       userForm.name.trim(),
        email:      userForm.email.trim(),
        role:       userForm.role,
        phone:      userForm.phone.trim(),
        company_id: selectedComp.id,
        company_name: selectedComp.company_name || "",
        status:     "active",
        active:     true,
        created_at: serverTimestamp(),
        created_by: SAAS_OWNER_UID,
      });
      setUserMsg(`✅ User created! UID: ${user.uid}`);
      setUserForm({ name: "", email: "", password: "Welcome@123", role: "owner", phone: "" });
      fetchCompanyUsers(selectedComp.id);
    } catch (err) {
      setUserMsg("❌ " + (err.code === "auth/email-already-in-use" ? "Email already exists" : err.message));
    } finally { setUserSaving(false); }
  };

  const toggleCompanyStatus = async (comp) => {
    const newStatus = comp.status === "active" ? "suspended" : "active";
    if (!window.confirm(`${newStatus === "suspended" ? "Suspend" : "Activate"} ${comp.company_name}?`)) return;
    await updateDoc(doc(db, "companies", comp.id), { status: newStatus });
    fetchCompanies();
  };

  const fmtDate = (ts) => {
    if (!ts?.seconds) return "-";
    return new Date(ts.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const MODULES = [
    { key: "service",     label: "🔧 Service Management",  desc: "Jobs, technicians, service dashboard" },
    { key: "inventory",   label: "📦 Store & Inventory",   desc: "MRF, stock, issue, returns" },
    { key: "procurement", label: "🛒 Procurement",         desc: "Purchase orders, vendors, GRN" },
    { key: "billing",     label: "🧾 Billing",             desc: "Invoices, payments, AMC billing" },
    { key: "accounts",    label: "💰 Accounts",            desc: "Ledger, expenses, Tally export" },
    { key: "projects",    label: "📁 Projects & CRM",      desc: "Projects, quotations, enquiries, clients" },
    { key: "assets",      label: "🛠️ Assets & Tools",     desc: "Tool register, transfers, machines" },
    { key: "amc",         label: "📋 AMC Contracts",       desc: "AMC contracts, OEM returns" },
    { key: "reports",     label: "📊 Reports",             desc: "Financial, inventory, project reports — OFF by default" },
    { key: "transport",   label: "🚛 Transport",           desc: "Transport log" },
    { key: "expenses",    label: "💸 Expenses",            desc: "Expense submission & approval" },
    { key: "ai_boq_import", label: "🤖 AI BOQ Import (Premium)", desc: "AI-assisted BOQ parsing — premium add-on, OFF by default" },
  ];

  return (
    <div style={{ padding: "24px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "linear-gradient(135deg, #0f172a, #1e40af)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>🏢</div>
        <div>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>ZENO — Super Admin</h1>
          <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#94a3b8" }}>SAAS Control Panel • Restricted Access</p>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0", padding: "4px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: "700" }}>
            {companies.length} Companies
          </span>
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        {[
        { key: "companies", label: "🏢 All Companies" },
        { key: "create",    label: "➕ New Company"   },
        { key: "migrate",   label: "🔧 Fix Asset Ledger" },
      ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: "8px 18px", borderRadius: "10px", border: "none", cursor: "pointer", fontWeight: "600", fontSize: "13px",
            background: activeTab === t.key ? "#1e40af" : "#fff",
            color:      activeTab === t.key ? "#fff"    : "#64748b",
            boxShadow:  activeTab === t.key ? "0 2px 8px rgba(30,64,175,0.3)" : "none",
          }}>{t.label}</button>
        ))}
      </div>

      {/* COMPANIES TAB */}
      {activeTab === "companies" && (
        <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>Loading...</div>
          ) : companies.length === 0 ? (
            <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>No companies yet.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {["Company", "Contact", "Plan", "Status", "Created", "Actions"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.map((c, idx) => (
                  <tr key={c.id} style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                    <td style={{ padding: "14px 16px" }}>
                      <p style={{ margin: 0, fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>{c.company_name}</p>
                      <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#94a3b8", fontFamily: "monospace" }}>{c.id}</p>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <p style={{ margin: 0, fontSize: "13px", color: "#374151" }}>{c.contact_name}</p>
                      <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#94a3b8" }}>{c.contact_phone}</p>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{ background: c.plan === "enterprise" ? "#ede9fe" : "#f1f5f9", color: c.plan === "enterprise" ? "#7c3aed" : "#64748b", padding: "3px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700", textTransform: "uppercase" }}>{c.plan || "basic"}</span>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <span style={{ background: c.status === "active" ? "#dcfce7" : "#fee2e2", color: c.status === "active" ? "#15803d" : "#dc2626", padding: "3px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>{c.status || "active"}</span>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: "12px", color: "#64748b" }}>{fmtDate(c.created_at)}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        <button onClick={(e) => { e.stopPropagation(); setSelectedComp(c); fetchCompanyUsers(c.id); setShowUserModal(true); }} style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#eff6ff", color: "#1d4ed8", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>👤 Users</button>
                        <button onClick={(e) => { e.stopPropagation(); setSelectedComp(c); setShowModuleModal(true); }} style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#f0fdf4", color: "#15803d", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>🧩 Modules</button>
                        <button onClick={(e) => { e.stopPropagation(); toggleCompanyStatus(c); }} style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: c.status === "active" ? "#fef2f2" : "#f0fdf4", color: c.status === "active" ? "#dc2626" : "#15803d", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>{c.status === "active" ? "Suspend" : "Activate"}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* CREATE COMPANY TAB */}
      {activeTab === "create" && (
        <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "28px", maxWidth: "600px" }}>
          <h2 style={{ margin: "0 0 20px", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>➕ Create New Company</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <Field label="Company Name *"  value={compForm.company_name}  onChange={v => setCompForm(p => ({ ...p, company_name: v }))}  placeholder="ABC HVAC Pvt Ltd" />
            <Field label="Contact Name *"  value={compForm.contact_name}  onChange={v => setCompForm(p => ({ ...p, contact_name: v }))}  placeholder="Ramesh Kumar" />
            <Field label="Contact Phone"   value={compForm.contact_phone} onChange={v => setCompForm(p => ({ ...p, contact_phone: v }))} placeholder="9876543210" />
            <Field label="Contact Email"   value={compForm.contact_email} onChange={v => setCompForm(p => ({ ...p, contact_email: v }))} placeholder="owner@abc.com" />
            <Field label="Business Type"   value={compForm.business_type} onChange={v => setCompForm(p => ({ ...p, business_type: v }))} placeholder="HVAC, Electrical..." />
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Plan</label>
              <select value={compForm.plan} onChange={e => setCompForm(p => ({ ...p, plan: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "14px", outline: "none", background: "#f8fafc" }}>
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <button onClick={handleCreateCompany} disabled={compSaving} style={{ padding: "12px", borderRadius: "12px", border: "none", background: compSaving ? "#e2e8f0" : "linear-gradient(135deg, #1e40af, #6366f1)", color: compSaving ? "#94a3b8" : "#fff", fontWeight: "700", fontSize: "14px", cursor: compSaving ? "not-allowed" : "pointer" }}>
              {compSaving ? "Creating..." : "Create Company"}
            </button>
          </div>
        </div>
      )}

      {/* MIGRATE TAB — Fix Asset Ledger */}
      {activeTab === "migrate" && (
        <MigrateAssetLedger />
      )}

      {/* ════ USER MODAL ════ */}
      {showUserModal && selectedComp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "16px" }}>
          <div style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "520px", maxHeight: "90vh", overflowY: "auto", padding: "28px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>{selectedComp.company_name}</h3>
                <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#94a3b8", fontFamily: "monospace" }}>{selectedComp.id}</p>
              </div>
              <button onClick={() => { setShowUserModal(false); setUserMsg(""); }} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "8px" }}>Existing Users ({compUsers.length})</p>
              {usersLoading ? <p style={{ fontSize: "13px", color: "#94a3b8" }}>Loading...</p>
              : compUsers.length === 0 ? <p style={{ fontSize: "13px", color: "#94a3b8", fontStyle: "italic" }}>No users yet</p>
              : (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {compUsers.map(u => (
                    <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      <div>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#0f172a" }}>{u.name}</p>
                        <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>{u.email}</p>
                      </div>
                      <span style={{ background: u.role === "owner" ? "#fef3c7" : "#eff6ff", color: u.role === "owner" ? "#d97706" : "#1d4ed8", padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>{u.role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "20px" }}>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "12px" }}>Add New User</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Field label="Full Name *" value={userForm.name}     onChange={v => setUserForm(p => ({ ...p, name: v }))}     placeholder="Ramesh Kumar" />
                <Field label="Email *"     value={userForm.email}    onChange={v => setUserForm(p => ({ ...p, email: v }))}    placeholder="owner@abc.com" type="email" />
                <Field label="Password"    value={userForm.password} onChange={v => setUserForm(p => ({ ...p, password: v }))} placeholder="Min 6 chars" type="password" />
                <Field label="Phone"       value={userForm.phone}    onChange={v => setUserForm(p => ({ ...p, phone: v }))}    placeholder="9876543210" />
                <div>
                  <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Role</label>
                  <select value={userForm.role} onChange={e => setUserForm(p => ({ ...p, role: e.target.value }))} style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "14px", outline: "none", background: "#f8fafc" }}>
                    {["owner","sm","manager","accounts","technician","store"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                {userMsg && <p style={{ fontSize: "13px", color: userMsg.startsWith("✅") ? "#15803d" : "#dc2626", fontWeight: "600", margin: 0 }}>{userMsg}</p>}
                <button onClick={handleCreateUser} disabled={userSaving} style={{ padding: "11px", borderRadius: "10px", border: "none", background: userSaving ? "#e2e8f0" : "linear-gradient(135deg, #059669, #10b981)", color: userSaving ? "#94a3b8" : "#fff", fontWeight: "700", fontSize: "14px", cursor: userSaving ? "not-allowed" : "pointer" }}>
                  {userSaving ? "Creating..." : "Create User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════ MODULE MODAL ════ */}
      {showModuleModal && selectedComp && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "16px" }}>
          <div style={{ background: "#fff", borderRadius: "20px", width: "100%", maxWidth: "480px", maxHeight: "90vh", overflowY: "auto", padding: "28px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>🧩 Modules — {selectedComp.company_name}</h3>
                <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8" }}>Toggle modules on/off for this company</p>
              </div>
              <button onClick={() => setShowModuleModal(false)} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            {MODULES.map(mod => {
              const isOn = isModuleOn(selectedComp.modules, mod.key);
              return (
                <div key={mod.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div>
                    <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#0f172a" }}>{mod.label}</p>
                    <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#94a3b8" }}>{mod.desc}</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newModules = { ...(selectedComp.modules || {}), [mod.key]: !isOn };
                      await updateDoc(doc(db, "companies", selectedComp.id), { modules: newModules });
                      setSelectedComp(prev => ({ ...prev, modules: newModules }));
                      setCompanies(prev => prev.map(c => c.id === selectedComp.id ? { ...c, modules: newModules } : c));
                    }}
                    style={{ width: "52px", height: "28px", borderRadius: "999px", border: "none", cursor: "pointer", position: "relative", background: isOn ? "#06b6d4" : "#e2e8f0", flexShrink: 0 }}>
                    <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#fff", position: "absolute", top: "4px", left: isOn ? "28px" : "4px", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                  </button>
                </div>
              );
            })}
            <button onClick={() => setShowModuleModal(false)} style={{ width: "100%", marginTop: "20px", padding: "11px", borderRadius: "10px", border: "none", background: "#0f172a", color: "#fff", fontWeight: "700", fontSize: "14px", cursor: "pointer" }}>
              Done ✅
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
function MigrateAssetLedger() {
  const [running,  setRunning]  = useState(false);
  const [log,      setLog]      = useState([]);
  const [done,     setDone]     = useState(false);

  const runMigration = async () => {
    if (!window.confirm("Ye migration sabhi companies ke assets fix karega. Proceed?")) return;
    setRunning(true);
    setLog([]);
    setDone(false);

    try {
      // Step 1 — Sabhi assets fetch karo
      const assetSnap = await getDocs(collection(db, "asset_master"));
      const assets = assetSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLog(prev => [...prev, `📦 Total assets found: ${assets.length}`]);

      let fixed = 0;
      let skipped = 0;

      for (const asset of assets) {
        // Step 2 — Check karo INITIAL_STOCK transaction exists ya nahi
        const txSnap = await getDocs(query(
          collection(db, "asset_transactions"),
          where("asset_id",      "==", asset.id),
          where("movement_type", "==", "INITIAL_STOCK")
        ));

        if (!txSnap.empty) {
          // INITIAL_STOCK exists — check karo workflow_state set hai ya nahi
          const txDoc = txSnap.docs[0];
          const txData = txDoc.data();
          if (!txData.workflow_state) {
            // workflow_state missing — update karo
            await updateDoc(doc(db, "asset_transactions", txDoc.id), {
              workflow_state: "FINALIZED",
              updated_at:     serverTimestamp(),
            });
            fixed++;
            setLog(prev => [...prev, `🔧 Updated workflow_state: ${asset.asset_name} (${asset.serial_no || asset.id})`]);
          } else {
            skipped++;
          }
          continue;
        }

        // Step 3 — INITIAL_STOCK transaction create karo
        await addDoc(collection(db, "asset_transactions"), {
          asset_id:          asset.id,
          asset_name:        asset.asset_name        || "",
          serial_no:         asset.serial_no         || "",
          movement_type:     "INITIAL_STOCK",
          from_type:         null,
          from_id:           null,
          from_holder_name:  null,
          to_type:           asset.current_holder_type || "STORE",
          to_id:             asset.current_holder_id   || "MAIN_STORE",
          to_holder_name:    null,
          status:            "FINALIZED",
          workflow_state:    "FINALIZED",
          company_id:        asset.company_id,
          requested_by:      "MIGRATION",
          requested_by_name: "SuperAdmin Migration",
          approved_by:       "MIGRATION",
          remarks:           "Auto-migrated — INITIAL_STOCK entry created from asset_master state",
          created_at:        serverTimestamp(),
          updated_at:        serverTimestamp(),
        });

        fixed++;
        setLog(prev => [...prev, `✅ Fixed: ${asset.asset_name} (${asset.serial_no || asset.id})`]);
      }

      setLog(prev => [
        ...prev,
        ``,
        `━━━━━━━━━━━━━━━━━━━━`,
        `✅ Fixed:   ${fixed} assets`,
        `⏭ Skipped: ${skipped} assets (already had INITIAL_STOCK)`,
        `━━━━━━━━━━━━━━━━━━━━`,
      ]);
      setDone(true);

    } catch (err) {
      setLog(prev => [...prev, `❌ Error: ${err.message}`]);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "28px", maxWidth: "640px" }}>
      <h2 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>🔧 Fix Asset Ledger</h2>
      <p style={{ margin: "0 0 20px", fontSize: "13px", color: "#64748b" }}>
        Existing assets jinke paas <code>INITIAL_STOCK</code> transaction nahi hai — unke liye create karega.
        Asset master ka current holder state use karega. Safe — already fixed assets skip honge.
      </p>

      <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px" }}>
        <p style={{ margin: 0, fontSize: "13px", color: "#92400e", fontWeight: "600" }}>
          ⚠️ SuperAdmin only — Production pe run karo carefully. One-time migration hai.
        </p>
      </div>

      <button
        onClick={runMigration}
        disabled={running || done}
        style={{
          padding: "12px 24px", borderRadius: "10px", border: "none",
          background: done ? "#d1fae5" : running ? "#e2e8f0" : "#1e40af",
          color: done ? "#065f46" : running ? "#94a3b8" : "#fff",
          fontWeight: "700", fontSize: "14px",
          cursor: (running || done) ? "not-allowed" : "pointer",
        }}
      >
        {done ? "✅ Migration Complete" : running ? "Running..." : "🚀 Run Migration"}
      </button>

      {log.length > 0 && (
        <div style={{
          marginTop: "20px", background: "#0f172a", borderRadius: "10px",
          padding: "16px", maxHeight: "400px", overflowY: "auto",
        }}>
          {log.map((line, i) => (
            <div key={i} style={{ fontSize: "12px", color: line.startsWith("✅") ? "#4ade80" : line.startsWith("❌") ? "#f87171" : line.startsWith("⏭") ? "#94a3b8" : "#e2e8f0", fontFamily: "monospace", lineHeight: "1.8" }}>
              {line || "\u00A0"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: "6px" }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "14px", color: "#0f172a", background: "#f8fafc", outline: "none", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = "#6366f1"}
        onBlur={e => e.target.style.borderColor = "#e2e8f0"}
      />
    </div>
  );
}